/**
 * Registro dos setores das folhas de apontamento.
 *
 * Cada setor é configuração: acrescentar um setor é acrescentar uma entrada.
 * As colunas e as datas-limite são as mesmas que as abas APONTAMENTO da
 * planilha GERENCIADOR DE PRODUÇÃO imprimem hoje (decisão do Igor, 22/10:
 * datas iguais às da planilha, inclusive a Expedição na CJ, sem a CQ).
 * As cores e as larguras de coluna (`grade`) foram aprovadas na fábrica.
 */
import { F } from "../gerencialFields";

// Tipos de coluna: o componente da folha decide o desenho de cada um.
// Terceira célula da faixa de informações: "total" (m², peças e ambientes),
// "itens" (Terceiros: N itens em M ambientes) ou "total_e_itens"
// (Acabamento: m² com "peças · N itens no acabamento").
export const TERCEIRA_CELULA = Object.freeze({ TOTAL: "total", ITENS: "itens", TOTAL_E_ITENS: "total_e_itens" });

export const TIPO_COLUNA = Object.freeze({
  CLIENTE: "cliente",
  AMBIENTE: "ambiente",
  LOTE: "lote",
  PROJETO: "projeto",
  PRAZO: "prazo",
  MDF: "mdf",
  PECAS: "pecas",
  DATA: "data", // data com o dia da semana embaixo
  MANUAL: "manual", // em branco para a caneta, salvo se a planilha já tiver
  ITEM: "item", // checklist só com a quantidade
  ITEM_DESCRITO: "item_descrito", // checklist com quantidade e descrição
  LIMITE: "limite", // data-limite do setor, com fundo tint
  ENTREGA: "entrega", // entrega no cliente, em vermelho
});

const T = TIPO_COLUNA;

const CLIENTE = { id: "cliente", rotulo: "Cliente", tipo: T.CLIENTE };
const AMBIENTE = { id: "ambiente", rotulo: "Ambiente", tipo: T.AMBIENTE };
const LOTE = { id: "lote", rotulo: "Lote", tipo: T.LOTE };
const PROJETO = { id: "projeto", rotulo: "Projeto", tipo: T.PROJETO };
const PRAZO = { id: "prazo", rotulo: "Prazo", tipo: T.PRAZO };
const MDF = { id: "mdf", rotulo: "MDF m²", tipo: T.MDF };
const PECAS = { id: "pecas", rotulo: "Peças", tipo: T.PECAS };

const COMUNS = [CLIENTE, AMBIENTE, LOTE, PROJETO, PRAZO, MDF, PECAS];
// Terceiros e Acabamento: a folha é de um lote só e o número já está no selo
// do cabeçalho, então a coluna Lote dá lugar às descrições.
const COMUNS_SEM_LOTE = COMUNS.filter((coluna) => coluna !== LOTE);

function limite(rotulo, campo) {
  return { id: "limite", rotulo, tipo: T.LIMITE, campo };
}

export const SETORES = Object.freeze([
  {
    id: "compras",
    titulo: "Compras & Estoques",
    curto: "Compras",
    cor: { accent: "#7E1F86", deep: "#5E1564", tint: "#F6EAF7" },
    rotuloLimite: "Limite de fornecimento",
    campoLimite: F.LIMITE_FORNECIMENTO,
    grade: "minmax(0,1fr) 150px 48px 64px 50px 58px 54px 100px 92px 92px 120px",
    colunas: [
      ...COMUNS,
      { id: "ordem_compra", rotulo: "Ordem de compra", tipo: T.DATA, campo: F.ORDEM_DE_COMPRA },
      { id: "mdf_bordo", rotulo: "MDF / Bordo", tipo: T.MANUAL, campo: F.MDF_BORDO },
      { id: "insumos", rotulo: "Insumos / Ferragens", tipo: T.MANUAL, campo: F.INSUMOS_FERRAGENS },
      limite("Limite de fornecimento", F.LIMITE_FORNECIMENTO),
    ],
  },
  {
    id: "maquinas",
    titulo: "Máquinas",
    curto: "Máquinas",
    cor: { accent: "#3F6B26", deep: "#2C4F19", tint: "#EBF2E5" },
    rotuloLimite: "Limite das máquinas",
    campoLimite: F.FINAL_MAQUINAS,
    grade: "minmax(0,1fr) 140px 48px 64px 50px 58px 54px 90px 90px 90px 90px 120px",
    colunas: [
      ...COMUNS,
      { id: "corte", rotulo: "Corte", tipo: T.MANUAL, campo: F.DATA_DE_CORTE },
      { id: "bordo", rotulo: "Bordo", tipo: T.MANUAL, campo: F.DATA_DE_BORDO },
      { id: "furacao", rotulo: "Furação", tipo: T.MANUAL, campo: F.DATA_DE_FURACAO },
      { id: "usinagem", rotulo: "Usinagem", tipo: T.MANUAL, campo: F.DATA_DE_USINAGEM },
      limite("Limite das máquinas", F.FINAL_MAQUINAS),
    ],
  },
  {
    id: "marcenaria",
    titulo: "Marcenaria",
    curto: "Marcenaria",
    cor: { accent: "#A8470F", deep: "#7E340A", tint: "#FBEDE3" },
    rotuloLimite: "Limite da marcenaria",
    campoLimite: F.FINAL_MARCENARIA_REPROGRAMADO,
    grade: "minmax(0,1fr) 100px 40px 58px 42px 50px 46px 60px 64px 64px 56px 84px 60px 60px 110px",
    compacta: true,
    colunas: [
      ...COMUNS,
      // BQ-BT: as colunas que a aba APONTAMENTO MARCENARIA imprime (=AC, =AD,
      // =AG, =AE). Lidas como texto, guardam o "X" de não se aplica.
      { id: "caixaria", rotulo: "Caixaria", tipo: T.ITEM, campo: F.CAIXARIA_MARC },
      { id: "tamburato", rotulo: "Tamburato", tipo: T.ITEM, campo: F.TAMBURATO_MARC },
      { id: "acab_manual", rotulo: "Acab. manual", tipo: T.ITEM, campo: F.ACABAMENTO_MANUAL_PORTA },
      { id: "ripado", rotulo: "Ripado", tipo: T.ITEM, campo: F.RIPADO_MARC },
      { id: "marceneiro", rotulo: "Marceneiro", tipo: T.MANUAL, campo: F.MARCENEIRO_RESPONSAVEL },
      { id: "inicio", rotulo: "Início", tipo: T.MANUAL, campo: F.MARCENARIA_INICIO },
      { id: "termino", rotulo: "Término", tipo: T.MANUAL, campo: F.MARCENARIA_TERMINO },
      limite("Limite da marcenaria", F.FINAL_MARCENARIA_REPROGRAMADO),
    ],
  },
  {
    id: "terceiros",
    titulo: "Terceiros & Componentes",
    curto: "Terceiros",
    cor: { accent: "#8A6508", deep: "#684C06", tint: "#F8F0DC" },
    rotuloLimite: "Limite dos terceiros",
    campoLimite: F.ENTREGA_TERCEIROS,
    grade: "minmax(0,1fr) 100px 54px 38px 44px 42px 96px 96px 96px 96px 96px 96px 100px",
    exigeItens: true,
    terceiraCelula: "itens",
    colunas: [
      ...COMUNS_SEM_LOTE,
      // Quantidade e descritivo do bloco INFO DO LOTE (AL-AW). As colunas
      // BI-BN não servem: viram data quando o pedido ao fornecedor é feito.
      { id: "serralheria", rotulo: "Serralheria", tipo: T.ITEM_DESCRITO, campo: F.SERRALHERIA_QTD, campoDescricao: F.SERRALHERIA_DESCRITIVO },
      { id: "perfil", rotulo: "Perfil alumínio pintura", tipo: T.ITEM_DESCRITO, campo: F.PERFIL_ALUMINIO_QTD, campoDescricao: F.PERFIL_ALUMINIO_DESCRITIVO },
      { id: "portas_frentes", rotulo: "Portas e frentes alumínio", tipo: T.ITEM_DESCRITO, campo: F.PORTAS_FRENTES_QTD, campoDescricao: F.PORTAS_FRENTES_DESCRITIVO },
      { id: "vidracaria", rotulo: "Vidraçaria", tipo: T.ITEM_DESCRITO, campo: F.VIDRACARIA_QTD, campoDescricao: F.VIDRACARIA_DESCRITIVO },
      { id: "tapecaria", rotulo: "Tapeçaria", tipo: T.ITEM_DESCRITO, campo: F.TAPECARIA_QTD, campoDescricao: F.TAPECARIA_DESCRITIVO },
      { id: "ferragens", rotulo: "Ferragens especiais", tipo: T.ITEM_DESCRITO, campo: F.FERRAGENS_ESPECIAIS_CELULA, campoDescricao: F.FERRAGENS_ESPECIAIS_DESCRITIVO },
      limite("Limite dos terceiros", F.ENTREGA_TERCEIROS),
    ],
  },
  {
    id: "acabamento",
    titulo: "Acabamento",
    curto: "Acabamento",
    cor: { accent: "#284A85", deep: "#1C3664", tint: "#E8EEF8" },
    rotuloLimite: "Limite do acabamento",
    campoLimite: F.FINAL_ACABAMENTO_REPROGRAMADO,
    grade: "minmax(0,1fr) 110px 58px 42px 48px 46px 130px 130px 130px 130px 110px",
    exigeItens: true,
    terceiraCelula: "total_e_itens",
    colunas: [
      ...COMUNS_SEM_LOTE,
      // Pintura = LACA (AI). Laca e verniz dividem um descritivo só (AK).
      { id: "pintura", rotulo: "Pintura", tipo: T.ITEM_DESCRITO, campo: F.LACA_QTD, campoDescricao: F.ACABAMENTO_DESCRITIVO },
      { id: "verniz", rotulo: "Verniz", tipo: T.ITEM_DESCRITO, campo: F.VERNIZ_QTD, campoDescricao: F.ACABAMENTO_DESCRITIVO },
      { id: "pintura_perfil", rotulo: "Pintura perfil", tipo: T.ITEM_DESCRITO, campo: F.PERFIL_ALUMINIO_QTD, campoDescricao: F.PERFIL_ALUMINIO_DESCRITIVO },
      { id: "pintura_serralheria", rotulo: "Pintura serralheria", tipo: T.ITEM_DESCRITO, campo: F.SERRALHERIA_QTD, campoDescricao: F.SERRALHERIA_DESCRITIVO },
      limite("Limite do acabamento", F.FINAL_ACABAMENTO_REPROGRAMADO),
    ],
  },
  {
    id: "expedicao",
    titulo: "Expedição",
    curto: "Expedição",
    cor: { accent: "#4A4542", deep: "#2E2A28", tint: "#EFEDEB" },
    rotuloLimite: "Limite da expedição",
    campoLimite: F.EXPEDICAO,
    grade: "minmax(0,1fr) 120px 48px 64px 50px 58px 54px 86px 80px 116px 86px 116px",
    colunas: [
      ...COMUNS,
      { id: "limpeza", rotulo: "Limpeza / Embalagem", tipo: T.MANUAL, campo: F.LIMPEZA_EMBALAGEM },
      { id: "separacao", rotulo: "Separação", tipo: T.MANUAL, campo: F.SEPARACAO },
      limite("Expedição", F.EXPEDICAO),
      { id: "carregamento", rotulo: "Carregamento", tipo: T.MANUAL, campo: F.CARREGAMENTO },
      { id: "entrega", rotulo: "Entrega cliente", tipo: T.ENTREGA, campo: F.DATA_REPROGRAMADA },
    ],
  },
]);

export const IDS_DOS_SETORES = Object.freeze(SETORES.map((setor) => setor.id));

/**
 * Formulário de Carregamento e Entrega (A4 retrato): a 7ª folha da matriz.
 * Não é um setor da produção (não tem colunas nem limite) e não entra no
 * preset "Todos"; só Cliente, Lote e os nomes dos ambientes vêm do lote.
 */
export const CARREGAMENTO = Object.freeze({
  id: "carregamento",
  titulo: "Carregamento e Entrega",
  curto: "Carregamento",
  retrato: true,
  cor: { accent: "#4A4542", deep: "#2E2A28", tint: "#F5F3F1" },
});

/** Colunas da matriz de impressão: os 6 setores e o formulário. */
export const IDS_DA_MATRIZ = Object.freeze([...IDS_DOS_SETORES, CARREGAMENTO.id]);

const POR_ID = new Map(SETORES.map((setor) => [setor.id, setor]));

export function setorPorId(id) {
  const setor = id === CARREGAMENTO.id ? CARREGAMENTO : POR_ID.get(id);
  if (!setor) throw new TypeError(`Setor desconhecido: ${String(id)}.`);
  return setor;
}

/** Entrega no cliente: a data reprogramada (CR), a mesma das abas APONTAMENTO. */
export const CAMPO_ENTREGA = F.DATA_REPROGRAMADA;

export const PRESETS = Object.freeze({
  todos: IDS_DOS_SETORES,
  producao: Object.freeze(["maquinas", "marcenaria", "acabamento", "expedicao"]),
  terceiros: Object.freeze(["terceiros"]),
  limpar: Object.freeze([]),
});
